import { Button, ButtonGroup, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { macroExamService } from 'services/api';
import { IMacroExam, IMedicalServiceBaseInfo } from 'interfaces';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { loadingActions } from 'features';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CollapsableColumns from 'components/common/CollapsableColumns';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import TenantPage from 'components/common/TenantPage';
import toast from 'features/toast';
import { PurgeButton } from 'components/helpers/PurgeButton';
import { isDirector } from 'components/helpers/IsDirector';
import { medicalServiceBaseInfoService } from 'services/api/MedicalServiceBaseInfoService';
import { MedicalServiceBaseInfoColumns } from 'components/common/enhanced/common-headers/medical-service-base-info';
import MedicalServiceBaseInfoForm from './MedicalServiceBaseInfoForm';
import MedicalServiceBaseInfoCreationForm from './MedicalServiceBaseInfoCreationForm';

export const MedicalServicesBaseInfo = () => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.medical-services-base-info.list';
  let tenantTitle = t(`${translationPrefix}.title`);

  const isDir = isDirector();
  if (isDir) {
    tenantTitle = tenantTitle.concat(' Emolife');
  }

  const dispatch = useAppDispatch();
  const authGroups = useAppSelector((state) => state.auth.groups);

  const datagridRefresh = useRef(null);

  const [isCreateSidePanelOpen, setIsCreateSidePanelOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [baseInfoValues, setBaseInfoValues] = useState<IMedicalServiceBaseInfo>();
  const [macroExams, setMacroExams] = useState<IMacroExam[]>([]);

  useEffect(() => {
    if (!isDir) {
      dispatch(loadingActions.startLoading());
      macroExamService
        .getAllPaginated()
        .then((res: { results?: IMacroExam[] }) =>
          setMacroExams(Array.isArray(res?.results) ? res.results : [])
        )
        .catch(() => setMacroExams([]))
        .finally(() => dispatch(loadingActions.stopLoading()));
      dispatch(loadingActions.stopLoading());
    }
  }, []);

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(!isUpdateDialogOpen);
  };

  const handleCloseCreateSidePanel = () => {
    setIsCreateSidePanelOpen(!isCreateSidePanelOpen);
  };

  const onSubmitCreateBaseInfo = async (values: IMedicalServiceBaseInfo) => {
    medicalServiceBaseInfoService.add(values).then((res) => {
      if (res) {
        toast.success(t('actions.successfully.created'));
      }
      datagridRefresh.current();
    });
  };

  const onSubmitUpdateBaseInfo = async (values: any, medicalServiceBaseInfoID: number) => {
    medicalServiceBaseInfoService.update(medicalServiceBaseInfoID, { ...values }).then((res) => {
      if (res) {
        toast.success(t('actions.successfully.updated'));
      }
      datagridRefresh.current();
    });
    handleCloseUpdateDialog();
  };

  const handleUpdateDialog = (row: IMedicalServiceBaseInfo) => {
    setIsUpdateDialogOpen(true);
    setBaseInfoValues(row);
  };

  // COLUMNS
  const columns = MedicalServiceBaseInfoColumns(authGroups, handleUpdateDialog);

  return (
    <>
      <TenantPage
        title={tenantTitle}
        menuRight={
          <div id="RightActionPanel" className="flex justify-end items-center">
            <div>
              <ButtonGroup>
                <IsTenantAdministrator>
                  <Button onClick={handleCloseCreateSidePanel}>
                    <AddIcon /> {t(`${translationPrefix}.add`)}
                  </Button>
                </IsTenantAdministrator>
              </ButtonGroup>
            </div>
            <PurgeButton service={medicalServiceBaseInfoService} refresh={datagridRefresh} />
          </div>
        }>
        <CollapsableColumns
          expand={isCreateSidePanelOpen}
          onClose={() => {
            setIsCreateSidePanelOpen(false);
          }}
          size="w-1/3"
          contentLeft={
            <EnhancedDataGrid
              refresh={datagridRefresh}
              service={medicalServiceBaseInfoService}
              columns={columns}
            />
          }
          contentRight={
            <MedicalServiceBaseInfoCreationForm
              onSubmit={onSubmitCreateBaseInfo}
              macroExams={macroExams}
            />
          }
        />

        <div id="DialogForm">
          <Dialog
            open={isUpdateDialogOpen}
            onClose={handleCloseUpdateDialog}
            fullWidth
            maxWidth="md">
            <DialogTitle>
              <div className="relative flex items-center justify-between">
                <div className="font-bold text-lg">{t(`${translationPrefix}.edit.title`)}</div>
                <IconButton
                  onClick={handleCloseUpdateDialog}
                  className="absolute right-0"
                  aria-label="close">
                  <CloseIcon />
                </IconButton>
              </div>
            </DialogTitle>
            <br></br>
            <DialogContent>
              <MedicalServiceBaseInfoForm
                onSubmit={onSubmitUpdateBaseInfo}
                macroExams={macroExams}
                medicalServiceBaseInfo={baseInfoValues}
              />
            </DialogContent>
          </Dialog>
        </div>
      </TenantPage>
    </>
  );
};

export default MedicalServicesBaseInfo;
